import { useState, useEffect } from "react";
import { MessageSquare, Copy, ExternalLink, Save, TestTube, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useDeliverySettings } from "@/hooks/useDeliverySettings";
import { usePublicMenuSettings } from "@/hooks/usePublicMenuSettings";
import { RestaurantWhatsAppStatus } from "@/components/settings/RestaurantWhatsAppStatus";
import { toast } from "sonner";

export function DeliveryAutoReplyCard() {
  const { 
    settings, 
    isUpdating, 
    updateSettings, 
    getWebhookUrl, 
    getMenuUrl,
    getDefaultGreeting,
    testAutoReply,
  } = useDeliverySettings();
  
  const { settings: publicMenuSettings } = usePublicMenuSettings();

  const [autoReplyEnabled, setAutoReplyEnabled] = useState(settings.autoReplyEnabled);
  const [deliveryWhatsappNumber, setDeliveryWhatsappNumber] = useState(settings.deliveryWhatsappNumber || "");
  const [greetingMessage, setGreetingMessage] = useState(settings.greetingMessage || "");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setAutoReplyEnabled(settings.autoReplyEnabled);
    setDeliveryWhatsappNumber(settings.deliveryWhatsappNumber || "");
    setGreetingMessage(settings.greetingMessage || "");
  }, [settings]);

  useEffect(() => {
    const changed =
      autoReplyEnabled !== settings.autoReplyEnabled ||
      deliveryWhatsappNumber !== (settings.deliveryWhatsappNumber || "") ||
      greetingMessage !== (settings.greetingMessage || "");
    setHasChanges(changed);
  }, [autoReplyEnabled, deliveryWhatsappNumber, greetingMessage, settings]);

  const handleSave = async () => {
    await updateSettings({
      autoReplyEnabled,
      deliveryWhatsappNumber: deliveryWhatsappNumber || null,
      greetingMessage: greetingMessage || null,
    });
    setHasChanges(false);
  };

  const copyMenuUrl = () => {
    navigator.clipboard.writeText(getMenuUrl());
    toast.success("Link do cardápio copiado!");
  };

  const useDefaultMessage = () => {
    setGreetingMessage(getDefaultGreeting());
  };

  const isDeliveryConfigured = publicMenuSettings.publicMenuEnabled && publicMenuSettings.deliveryEnabled;

  if (!isDeliveryConfigured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Resposta Automática WhatsApp (Delivery)
          </CardTitle>
          <CardDescription>
            Configure resposta automática para clientes que enviam mensagem
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Para usar esta funcionalidade, ative primeiro o <strong>Cardápio Público</strong> e o modo <strong>Delivery</strong> nas configurações acima.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Resposta Automática WhatsApp (Delivery)
        </CardTitle>
        <CardDescription>
          Quando um cliente envia mensagem para o WhatsApp, recebe automaticamente uma saudação com o link do cardápio
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Switch */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Ativar Resposta Automática</Label>
            <p className="text-sm text-muted-foreground">
              Responde automaticamente com link do cardápio quando receber mensagem
            </p>
          </div>
          <Switch
            checked={autoReplyEnabled}
            onCheckedChange={setAutoReplyEnabled}
          />
        </div>

        {autoReplyEnabled && (
          <>
            {/* WhatsApp Number */}
            <div className="space-y-2">
              <Label htmlFor="deliveryWhatsapp">Número do WhatsApp para Pedidos</Label>
              <Input
                id="deliveryWhatsapp"
                type="tel"
                placeholder="5516997096672"
                value={deliveryWhatsappNumber}
                onChange={(e) => setDeliveryWhatsappNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Número que receberá as mensagens dos clientes (com código do país, ex: 5516997096672)
              </p>
            </div>

            <Separator />

            {/* Greeting Message */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="greetingMessage">Mensagem de Saudação</Label>
                <Button variant="ghost" size="sm" onClick={useDefaultMessage}>
                  Usar padrão
                </Button>
              </div>
              <Textarea
                id="greetingMessage"
                placeholder="Digite a mensagem de saudação..."
                value={greetingMessage}
                onChange={(e) => setGreetingMessage(e.target.value)}
                rows={8}
                className="font-mono text-sm"
              />
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium">Placeholders disponíveis:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li><code className="bg-muted px-1 rounded">{"{restaurante}"}</code> - Nome do restaurante</li>
                  <li><code className="bg-muted px-1 rounded">{"{link}"}</code> - Link do cardápio</li>
                  <li><code className="bg-muted px-1 rounded">{"{tempo_estimado}"}</code> - Tempo de entrega configurado</li>
                  <li><code className="bg-muted px-1 rounded">{"{taxa_entrega}"}</code> - Taxa de entrega</li>
                  <li><code className="bg-muted px-1 rounded">{"{pedido_minimo}"}</code> - Pedido mínimo</li>
                </ul>
              </div>
            </div>

            <Separator />

            {/* Menu URL */}
            <div className="space-y-2">
              <Label>Link do Cardápio</Label>
              <div className="flex gap-2">
                <Input value={getMenuUrl()} readOnly className="flex-1 bg-muted" />
                <Button variant="outline" size="icon" onClick={copyMenuUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={getMenuUrl()} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <Separator />

            {/* Restaurant WhatsApp Instance (status / QR only) */}
            <RestaurantWhatsAppStatus />

            {/* Cooldown Info */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                O sistema não responde ao mesmo número mais de uma vez por hora para evitar spam.
              </AlertDescription>
            </Alert>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {hasChanges && (
            <Button onClick={handleSave} disabled={isUpdating} className="gap-2">
              <Save className="h-4 w-4" />
              {isUpdating ? "Salvando..." : "Salvar Alterações"}
            </Button>
          )}
          {autoReplyEnabled && !hasChanges && (
            <Button variant="outline" onClick={testAutoReply} className="gap-2">
              <TestTube className="h-4 w-4" />
              Verificar Configuração
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
